import { Menu, LogOut, Flame, Zap, Settings } from 'lucide-react';
import { useAuth } from '@lib/auth';
import { useUIStore } from '@stores/ui.store';
import { CHANGE_PASSWORD_MODAL_ID } from './ChangePasswordModal';
import { ThemeToggle } from '@components/ThemeToggle';

export function Header(): JSX.Element {
  const { user, logout } = useAuth();
  const { toggleSidebar, openModal } = useUIStore();

  const handleLogout = async (): Promise<void> => {
    await logout();
  };

  const handleOpenSettings = (): void => {
    openModal(CHANGE_PASSWORD_MODAL_ID);
  };

  return (
    <header className="h-16 glass flex items-center justify-between px-6">
      {/* Left side */}
      <div className="flex items-center gap-4">
        <button
          onClick={toggleSidebar}
          className="p-2.5 rounded-lg text-ybh-text-muted hover:bg-ybh-bg-tertiary hover:text-ybh-text-primary transition-colors duration-200"
          aria-label="Toggle sidebar"
        >
          <Menu size={20} />
        </button>

        {/* Breadcrumb / Page context could go here */}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Streak indicator */}
        {user && user.currentStreak > 0 && (
          <div className="flex shrink-0 items-center gap-2 whitespace-nowrap px-4 py-2 bg-ybh-warning/10 border border-ybh-warning/20 rounded-lg">
            <Flame size={16} className="text-ybh-warning" />
            <span className="text-sm font-semibold text-ybh-warning">
              {user.currentStreak}
            </span>
            <span className="hidden md:inline text-xs text-ybh-text-muted">day streak</span>
          </div>
        )}

        {/* Quick action indicator */}
        <div className="hidden lg:flex shrink-0 items-center gap-2 whitespace-nowrap px-4 py-2 bg-ybh-orange/10 border border-ybh-orange/20 rounded-lg">
          <Zap size={16} className="text-ybh-orange" />
          <span className="text-xs text-ybh-text-secondary">
            Press <kbd className="px-1.5 py-0.5 bg-ybh-bg-tertiary rounded text-ybh-orange font-mono text-[10px]">Q</kbd> to quick log
          </span>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-ybh-text-muted/20" />

        {/* Theme toggle */}
        <ThemeToggle />

        {/* Settings button */}
        <button
          onClick={handleOpenSettings}
          className="group flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg text-ybh-text-muted hover:bg-ybh-bg-tertiary hover:text-ybh-text-primary transition-colors duration-200"
          aria-label="Settings"
          title="Change Password"
        >
          <Settings size={18} />
          <span className="text-sm hidden lg:inline">Settings</span>
        </button>

        {/* Divider */}
        <div className="w-px h-8 bg-ybh-text-muted/20" />

        {/* Logout button */}
        <button
          onClick={handleLogout}
          className="group flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg text-ybh-text-muted hover:bg-ybh-error/10 hover:text-ybh-error transition-colors duration-200"
          aria-label="Logout"
        >
          <LogOut size={18} />
          <span className="text-sm hidden lg:inline">Sign out</span>
        </button>
      </div>
    </header>
  );
}
